import { UUID, uuidv4 } from "uuidv7"

import type { File } from "~/models/polyschema/File"

import { addFileToBuf } from "~/utils/flatbuffers/file"

import { apiIdFor, asTimestampMillis, dateFromTimestampUuid, EntityApiId, EntityMimeTypeId, groupFor, hasGroup, mediaType, mimeTypeString, PropertyGroup, PropertyId, PropertySchema, PropertyStatus, sharedWith } from "../entity"
import { BaseEntity } from "../entity/BaseEntity"
import { FileThumbnailKindBuf } from "../generated/file"
import { sendFolderSubscribeRequest } from "../handlers/file/subscribe"
import { FileUserRole } from "./properties/FileUserRole"

export abstract class BaseFile extends BaseEntity<FileProperty, File> {
  abstract path<T extends BaseFile>(): T[] | undefined

  static async tagPropertyId(tag: string) {
    if (!BaseFile.isTagValid(tag))
      throw new Error("Invalid tag")
    const encoder = new TextEncoder()
    const data = encoder.encode(tag)
    const hash = await window.crypto.subtle.digest("SHA-256", data)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    const groupStr = PropertyGroup.FileTagMetaProperty.toString(16).padStart(8, "0")
    return `${groupStr}-${uuid.toString()}` as PropertyId
  }

  /**
   * On the client, all file creation requires that we use a file id that comes from a
   * "pre-hashed" set of bytes. This id is the pre-hash, and we need to contact the
   * cache to help us find the resulting file id.
   * NOTE: this is not technically a "wrong" file id. In the future we might try to
   * make doubly sure that this is only a pre-hash id so that it doesn't get mixed
   * up, but that isn't really necessary.
   */
  static clientPreHashId() {
    return `00000000-${uuidv4()}`
  }

  /**
   * Generates a home id for a shared folder
   */
  static clientSharedHomePreHashId() {
    return `00001002-${uuidv4()}`
  }

  /**
   * Generates an archive id for a shared folder
   */
  static clientSharedArchivePreHashId() {
    return `00001003-${uuidv4()}`
  }

  /**
   * @returns a pre hash for a shared folder id
   */
  static sharedFolderPreHashId() {
    return `00020000-${uuidv4()}`
  }

  static toThumbnailUrl(s3ThumbnailUri?: string) {
    // TODO: replace with a placeholder or loading?
    if (!s3ThumbnailUri)
      return ""
    return s3ThumbnailUri
      // // This seems to not work on localhost
      // .replace("s3://store-with-poly-local", "http://s3.us-west-2.localhost.localstack.cloud:4566/store-with-poly-local")
      .replace("s3://store-with-poly-local", "http://s3.localhost.localstack.cloud:4566/store-with-poly-local")
      .replace("s3://store-with-poly-dev", "https://d2cezt94ra8046.cloudfront.net")
      .replace("s3://store-with-poly-prod", "https://d3b8el8c5kqwdk.cloudfront.net")
  }

  static getFilePath(fileId: string) {
    return `/file/id/${asShortId(fileId)}`
  }

  static getFolderPath(fileId: string) {
    return `/folder/id/${asShortId(fileId)}`
  }

  override toNavigationUrl(): string {
    if (this.isBlank() || !this.isFolder())
      return `/file/id/${asShortId(this.id)}`
    else
      return `/folder/id/${asShortId(this.id)}`
  }

  /**
   * Checks if the provided file tag value is valid according to our rules
   * - Must not contain any control characters or the following characters: <>"'{}`#$^
   * - Must not contain untrimmed whitespace
   * - Must not exceed 64 characters or be empty
   * - Must not contain a colon unless it is a color tag and must have a valid color
   */
  static isTagValid(tag: string): boolean {
    const COLORS = ["yellow", "green", "teal", "blue", "purple", "violet", "pink", "red", "gray"]
    const MAX_LENGTH = 64
    const MIN_LENGTH = 1

    // Check for untrimmed whitespace
    if (tag.trim() !== tag)
      return false
    if (tag.toLowerCase() !== tag)
      return false
    if (tag.length < MIN_LENGTH || tag.length > MAX_LENGTH)
      return false
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001F\u007F-\u009F<>"'{}`#$^]/.test(tag))
      return false

    // Check for color prefixes
    const parts = tag.split(":")
    if (parts.length > 2)
      return false
    else if (parts.length === 2) {
      const [color, suffix] = parts
      if (!COLORS.includes(color.toLowerCase()))
        return false
      if (suffix.trim() !== suffix)
        return false
    }

    return true
  }

  addToBuf(builder: DatagramBufferBuilder) {
    return addFileToBuf(builder, this.inner())
  }

  /**
   * Returns a file permission, if any, that is implied by this file's path
   */
  impliedUserRole(userId: string) {
    // short circuit if we are in our home/archive directory
    const { state: myself } = useSelf()
    const permissionChain: FileUserRole[] = []
    for (const file of [this, ...this.path() ?? []]) {
      const userRole = file.sharedWithUserRoleDetails(userId)?.role
      const publicRole = file.directPublicRole()
      if (isDefined(myself.value.home) && myself.value.home.id === file.id)
        permissionChain.push(FileUserRole.newOwner())
      else if (isDefined(myself.value.archive) && myself.value.archive.id === file.id)
        permissionChain.push(FileUserRole.newOwner())

      if (isDefined(userRole))
        permissionChain.push(userRole)
      else if (isDefined(publicRole))
        permissionChain.push(publicRole)
    }
    // we reverse the array since we want to reduce from highest (outer)
    // to lowest (inner), with a default of nothing (base). The inner
    // permission will always override the outer one.
    return permissionChain.reverse().reduce(
      (outer, inner) => outer.overlay(inner),
      FileUserRole.newBase(),
    )
  }

  impliedPublicRole() {
    const permissionChain: FileUserRole[] = []
    for (const file of [this, ...this.path() ?? []]) {
      const publicRole = file.directPublicRole()
      if (isDefined(publicRole))
        permissionChain.push(publicRole)
    }
    // we reverse the array since we want to reduce from highest (outer)
    // to lowest (inner), with a default of nothing (base). The inner
    // permission will always override the outer one.
    return permissionChain.reverse().reduce(
      (outer, lower) => lower.overlay(outer),
      FileUserRole.newBase(),
    )
  }

  /** This user's role, whether or not the user is signed in or not. */
  impliedSelfRole() {
    const { state: myself } = useSelf()
    if (isDefined(myself.value.user))
      return this.impliedUserRole(myself.value.user.id)
    return this.impliedPublicRole()
  }

  /** Gets the parent id by either looking for the parent property or looking at the path */
  inferredParentId(): string | undefined {
    if (this.isUserHomeFolder() || this.isUserArchiveFolder() || this.isSharedFolderHome() || this.isSharedFolderArchive())
      return undefined
    const property = this.property(PropertyId.FILE_PARENT_ID)
    if (property && property.value.type === PropertySchema.FileIdValue)
      return property.value.fileId
    // console.warn("No parent id property, looking in path for parent id")
    if (this.inner().path?.length === 0)
      return
    const parentId = this.inner().path?.[0]?.id
    if (parentId)
      return parentId
    throw new Error("Cannot retrieve parent id of file")
  }

  hasPath(): boolean {
    return !!this.inner().path
  }

  isArtifact(): boolean {
    if (!this.inferredParentId())
      return false
    return apiIdFor(this.inferredParentId()!) === EntityApiId.FileIdConversationArtifactsFolderV0
  }

  name(): string | undefined {
    if (this.isUserHomeFolder())
      return "Home"
    if (this.isUserArchiveFolder())
      return "Archive"
    const property = this.property(PropertyId.FILE_NAME)
    if (!property)
      return
    if (property.value.type === PropertySchema.NonEmptyStringValue)
      return property.value.nonEmptyString
    throw new Error("Schema invalid for name")
  }

  extension(): string | undefined {
    const name = this.name()
    if (!name)
      return
    if (!name.includes("."))
      return ""
    return last(name.split("."))
  }

  createdAt(): Date | undefined {
    const property = this.property(PropertyId.FILE_CREATED_AT)
    if (!property)
      return
    if (property.value.type === PropertySchema.UtcTimestampMillisValue) {
      return new Date(property.value.utcTimestampMillis)
    }
    throw new Error("Schema invalid for created at")
  }

  modifiedAt(): Date | undefined {
    const property = this.property(PropertyId.FILE_MODIFIED_AT)
    if (!property)
      return
    if (property.value.type === PropertySchema.UtcTimestampMillisValue) {
      return new Date(property.value.utcTimestampMillis)
    }
    throw new Error("Schema invalid for modified at")
  }

  accessedAt(): Date | undefined {
    const property = this.property(PropertyId.FILE_ACCESSED_AT)
    if (!property)
      return
    if (property.value.type === PropertySchema.UtcTimestampMillisValue) {
      return new Date(property.value.utcTimestampMillis)
    }
    throw new Error("Schema invalid for accessed at")
  }

  addedAt(): Date | undefined {
    const property = this.property(PropertyId.FILE_CREATED_AT)
    if (!property)
      return
    return dateFromTimestampUuid(property.timestamp)
  }

  mimeType(): EntityMimeType | undefined {
    const property = this.property(PropertyId.FILE_MIME_TYPE)
    if (!property)
      return
    if (property.value.type === PropertySchema.MimeTypeValue)
      return property.value.mimeType
    throw new Error("Schema invalid for name")
  }

  mediaType(): EntityMediaType | undefined {
    return maybe(mediaType)(this.mimeType())
  }

  checksum(): Uint8Array | undefined {
    const property = this.property(PropertyId.FILE_SHA256_CHECKSUM)
    if (!property)
      return
    if (property.value.type === PropertySchema.Sha256HashValue)
      return property.value.hash
    throw new Error("Schema invalid for checksum")
  }

  folderUsageSize(): number | undefined {
    const property = this.property(PropertyId.FOLDER_USAGE_SIZE)
    if (!property)
      return
    if (property.value.type === PropertySchema.SizeBytesValue)
      return property.value.sizeBytes
    console.error("Folder usage size is malformed", property)
  }

  folderUsageQuantity(): number | undefined {
    const property = this.property(PropertyId.FOLDER_USAGE_QUANTITY)
    if (!property)
      return
    if (property.value.type === PropertySchema.NonNegativeIntegerValue)
      return property.value.nonNegativeInteger
    console.error("Folder usage quantity is malformed", property)
  }

  folderUsageUpdatedAt(): number | undefined {
    const property = this.property(PropertyId.FOLDER_USAGE_UPDATED_AT)
    if (!property)
      return
    if (property.value.type === PropertySchema.UtcTimestampMillisValue)
      return property.value.utcTimestampMillis
    console.error("Folder usage updated at is malformed", property)
  }

  isFolder(): boolean {
    if (this.isUserHomeFolder() || this.isUserArchiveFolder() || this.isSharedFolderHome() || this.isSharedFolderArchive())
      return true
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "folder"
    console.error("Missing or invalid mime type")
    throw new Error(`Mime type property missing or invalid, can't know if this is a folder ${this.id}`)
  }

  isImage(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "image"
    console.warn("Unable to know if this file is an image")
    throw new Error(`Mime type property missing or invalid, can't know if this is a image ${this.id}`)
  }

  isSvg(): boolean {
    const mimeType = this.mimeType()
    if (mimeType)
      return mimeType.value === EntityMimeTypeId.ImageSvg
    console.warn("Unable to know if this file is an SVG")
    throw new Error(`Mime type property missing or invalid, can't know if this is a SVG ${this.id}`)
  }

  isAudio(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "audio"
    console.warn("Unable to know if this file is a audio")
    throw new Error(`Mime type property missing or invalid, can't know if this is a audio ${this.id}`)
  }

  isVideo(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "video"
    console.warn("Unable to know if this file is a video")
    throw new Error(`Mime type property missing or invalid, can't know if this is a video ${this.id}`)
  }

  isLink(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "link"
    console.warn("Unable to know if this file is a link")
    throw new Error(`Mime type property missing or invalid, can't know if this is a link ${this.id}`)
  }

  isVendorYoutubeLink(): boolean {
    const mimeType = this.mimeType()
    if (mimeType)
      return mimeType.value === EntityMimeTypeId.VendorYoutubeVideo
    console.warn("Unable to know if this file is a vendor youtube link")
    throw new Error(`Mime type property missing or invalid, can't know if this is a vendor youtube link ${this.id}`)
  }

  isPdf(): boolean {
    const mimeType = this.mimeType()
    if (mimeType)
      return mimeType.value === EntityMimeTypeId.ApplicationPdf
    console.warn("Unable to know if this file is a pdf")
    throw new Error(`Mime type property missing or invalid, can't know if this is a pdf ${this.id}`)
  }

  isDocument(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "document"
    console.warn("Unable to know if this file is a document")
    throw new Error(`Mime type property missing or invalid, can't know if this is a document ${this.id}`)
  }

  isSpreadsheet(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "spreadsheet"
    console.warn("Unable to know if this file is a document")
    throw new Error(`Mime type property missing or invalid, can't know if this is a document ${this.id}`)
  }

  isPresentation(): boolean {
    const mediaType = this.mediaType()
    if (mediaType)
      return mediaType === "presentation"
    console.warn("Unable to know if this file is a document")
    throw new Error(`Mime type property missing or invalid, can't know if this is a document ${this.id}`)
  }

  isText(): boolean {
    // links also start with 'text' so we need to exclude them
    const mimeType = this.mimeType()
    if (mimeType) {
      if (this.isLink())
        return false
      return mimeTypeString(mimeType).startsWith("text/")
    }
    throw new Error(`Mime type property missing or invalid, can't know if this is a text ${this.id}`)
  }

  thumbnail64Url() {
    const property = this.property(PropertyId.THUMBNAIL_SET)
    if (!property)
      return
    if (property.value.type !== PropertySchema.FileThumbnailSetValue) {
      console.error("Malformed URI", property)
      throw new Error("Malformed thumbnail Uri")
    }
    const image = property.value.set.images[FileThumbnailKindBuf.Native64]
    if (!image)
      return
    return BaseFile.toThumbnailUrl(image.uri)
  }

  downloadSourceUrl() {
    const property = this.property(PropertyId.FILE_DOWNLOAD_SOURCE_URL)
    if (!property)
      return
    if (property.value.type !== PropertySchema.WebUrlValue) {
      console.error("Malformed URI", property)
      throw new Error("Malformed download url")
    }
    return property.value.url
  }

  downloadSourceApplication() {
    const property = this.property(PropertyId.FILE_DOWNLOAD_SOURCE_APPLICATION)
    if (!property)
      return
    if (property.value.type !== PropertySchema.FileDownloadSourceApplicationValue) {
      console.error("Malformed property", property)
      throw new Error("Malformed download application")
    }
    return property.value.sourceApplication
  }

  /** Images and audio use imagebind, but others use the file pdf embeddings */
  supportsSimilaritySearch = () => {
    return this.isImage() || this.isAudio() || this.isPdf() || this.isDocument() || this.isPresentation() || this.isSpreadsheet() || this.isLink() || this.isText()
  }

  isUserHomeFolder() {
    return apiIdFor(this.id) === EntityApiId.FileIdUserHomeV0
  }

  isUserArchiveFolder() {
    return apiIdFor(this.id) === EntityApiId.FileIdUserArchiveV0
  }

  isSharedFolderHome() {
    return apiIdFor(this.id) === EntityApiId.FileIdSharedFolderHomeV0
  }

  isSharedFolderArchive() {
    return apiIdFor(this.id) === EntityApiId.FileIdSharedFolderArchiveV0
  }

  isConversationArtifactsFolder() {
    return apiIdFor(this.id) === EntityApiId.FileIdConversationArtifactsFolderV0
  }

  isRootFolder() {
    return this.isUserHomeFolder() || this.isUserArchiveFolder() || this.isSharedFolderHome() || this.isSharedFolderArchive()
  }

  uri() {
    const property = this.property(PropertyId.FILE_URI)
    // TODO: check for the item in the selection
    if (!property)
      throw new Error("no URI property present")
    if (property.value.type === PropertySchema.S3BlobUriValue)
      return property.value.s3BlobUri
    throw new Error("Property is not a uri type property")
  }

  size(): number | undefined {
    const property = this.property(PropertyId.FILE_SIZE)
    if (!property)
      return
    if (property.value.type === PropertySchema.SizeBytesValue)
      return property.value.sizeBytes
    console.error("No size property or its malformed", property)
  }

  isPermanentlyDeleted(): boolean {
    return !!this.property(PropertyId.FILE_IS_PERMANENTLY_DELETED)
  }

  mediaWidthPx() {
    const property = this.property(PropertyId.MEDIA_WIDTH_PX)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveIntegerValue)
      return property.value.positiveInteger
    console.error("image width is malformed", property)
  }

  mediaHeightPx() {
    const property = this.property(PropertyId.MEDIA_HEIGHT_PX)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveIntegerValue)
      return property.value.positiveInteger
    console.error("image height is malformed", property)
  }

  mediaWidthCm() {
    const property = this.property(PropertyId.MEDIA_WIDTH_CM)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveFloatValue)
      return property.value.positiveFloat
    console.error("image width is malformed", property)
  }

  mediaHeightCm() {
    const property = this.property(PropertyId.MEDIA_HEIGHT_CM)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveFloatValue)
      return property.value.positiveFloat
    console.error("image height is malformed", property)
  }

  aspectRatio() {
    let [width, height] = [this.mediaWidthPx(), this.mediaHeightPx()]
    if (!width || !height)
      [width, height] = [this.mediaWidthCm(), this.mediaHeightCm()]
    if (width && height)
      return width / height
  }

  mediaPages() {
    const property = this.property(PropertyId.MEDIA_PAGES)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveIntegerValue)
      return property.value.positiveInteger
    console.error("image height is malformed", property)
  }

  textSnippet() {
    const property = this.property(PropertyId.TEXT_SNIPPET)
    if (!property)
      return
    if (property.value.type === PropertySchema.AnyStringValue)
      return property.value.anyString
    console.error("text snippet is malformed", property)
  }

  textLineCount() {
    const property = this.property(PropertyId.TEXT_LINE_COUNT)
    if (!property)
      return
    if (property.value.type === PropertySchema.PositiveIntegerValue)
      return property.value.positiveInteger
    console.error("text line count is malformed", property)
  }

  linkDestinationUrl() {
    const property = this.property(PropertyId.LINK_DESTINATION_URL)
    if (!property)
      return
    if (property.value.type === PropertySchema.WebUrlValue)
      return property.value.url
    console.error("link destination is malformed", property)
  }

  rating() {
    const property = this.property(PropertyId.FILE_RATING)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.PositiveFloatValue)
      return property.value.positiveFloat
    console.error("File rating is malformed", property)
  }

  notes() {
    const property = this.property(PropertyId.FILE_NOTES)
    if (!property || property.deleted)
      return
    if (property && property.value.type === PropertySchema.AnyStringValue)
      return property.value.anyString
    console.error("File notes are malformed", property)
  }

  /** returns all tag properties, minus the deleted ones */
  tagProperties() {
    return Object
      .values(this.inner().properties)
      .filter(p => hasGroup(p!.propertyId as PropertyId, PropertyGroup.FileTagMetaProperty))
      .filter(p => !p!.deleted) as FileProperty[]
  }

  /**
   * There is no guaranteed sorting order here!
   */
  tags() {
    const tags = this
      .tagProperties()
      .map(p => p!.value.type === PropertySchema.HashableTagValue
        ? p!.value.hashableTag
        : undefined)
      .filter(t => t) as string[]
    return new Set(tags)
  }

  generationPrompt() {
    const property = this.property(PropertyId.FILE_GENERATION_PROMPT)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.NonEmptyStringValue)
      return property.value.nonEmptyString
    console.error("File generation prompt is malformed", property)
  }

  generationNegativePrompt() {
    const property = this.property(PropertyId.FILE_GENERATION_NEGATIVE_PROMPT)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.NonEmptyStringValue)
      return property.value.nonEmptyString
    console.error("File generation negative prompt is malformed", property)
  }

  generationModel() {
    const property = this.property(PropertyId.FILE_GENERATION_MODEL)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.NonEmptyStringValue)
      return property.value.nonEmptyString
    console.error("File generation model is malformed", property)
  }

  generationSource() {
    const property = this.property(PropertyId.FILE_GENERATION_SOURCE)
    if (!property)
      return
    if (property && property.value.type === PropertySchema.NonEmptyStringValue)
      return property.value.nonEmptyString
    console.error("File generation source is malformed", property)
  }

  matchedChunks() {
    const property = this.property(PropertyId.FILE_VIRTUAL_MATCHED_CHUNKS)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileMatchedChunksValue)
      return property.value.matchedChunks
    console.error("File matched chunks are malformed", property)
  }

  sharedWithProperties() {
    return Object
      .values(this.inner().properties)
      .filter(p => hasGroup(p!.propertyId as PropertyId, PropertyGroup.FileSharingUsersMetaProperty))
      .map(p => p!)
  }

  /** Mapping of user ids to roles */
  sharedWithUserRoles() {
    const userRoles: Record<string, {
      userId: string
      role: FileUserRole
      status: PropertyStatus
      version: number
      deleted?: boolean
    }> = {}
    for (const property of Object.values(this.sharedWithProperties())) {
      if (!property
        || property.value.type !== PropertySchema.RoleUuidValue
        || !property.sharedWithUserId
      )
        continue
      userRoles[property.sharedWithUserId] = {
        userId: property.sharedWithUserId,
        role: new FileUserRole(property.value.role),
        status: property.status,
        version: property.version,
        deleted: property.deleted,
      }
    }
    return userRoles
  }

  sharedWithUserRoleDetails(userId: string) {
    const property = this.property(sharedWith(userId))
    if (!isDefined(property))
      return
    if (property.value.type !== PropertySchema.RoleUuidValue)
      throw new Error("Property is not a role uuid type property")
    return {
      userId: property.sharedWithUserId,
      role: new FileUserRole(property.value.role),
      status: property.status,
      version: property.version,
      deleted: property.deleted,
    }
  }

  /** The exact public role for this file, without considering its path. */
  directPublicRole() {
    const property = this.property(PropertyId.FILE_PUBLIC_USER_ROLE)
    if (!isDefined(property))
      return
    if (property.value.type !== PropertySchema.RoleUuidValue)
      throw new Error("Property is not a role uuid type property")
    return new FileUserRole(property.value.role)
  }

  /** Gets the shared shared folder that owns this file, if any */
  rootSharedFolderId(): string | undefined {
    const owner = this.property(PropertyId.SHARED_FOLDER_OWNER_ID)
    if (owner && owner.value.type === PropertySchema.SharedFolderIdValue)
      return owner.value.sharedFolderId
    const path = this.path()
    if (path?.length)
      return last(path as this[])!.rootSharedFolderId()
  }

  rootSharedFolderHandle(): EntityHandle | undefined {
    const handleProp = this.property(PropertyId.FILE_SHARED_FOLDER_HANDLE)
    if (isDefined(handleProp) && handleProp.value.type === PropertySchema.EntityHandleValue)
      return handleProp.value.entityHandle
    const path = this.path()
    if (path?.length)
      return last(path as this[])!.rootSharedFolderHandle()
  }

  isNestedWithin(folderId: string) {
    if (!this.inner().path)
      throw new Error("Cannot determine contained-by question without path")
    const parents = this.inner().path!.map(f => f.id)
    return parents.includes(folderId)
  }

  /**
   * Returns true if the file has a certain property or virtual
   * property that is not deleted
   */
  hasProperty(property: PropertyId) {
    switch (property) {
      case (PropertyId.FILE_VIRTUAL_DIMENSIONS):
        return this.isImage() || this.isPdf() || this.isVideo() || this.isAudio()
      case (PropertyId.FILE_VIRTUAL_MEDIA_TYPE):
        return isDefined(this.mimeType())
      case (PropertyId.FILE_VIRTUAL_ADDED_AT):
        return isDefined(this.addedAt())
      case (PropertyId.FILE_VIRTUAL_PATH):
        return true
      case (PropertyId.FILE_VIRTUAL_PREVIEW):
        return true
      case (PropertyId.FILE_VIRTUAL_SELF_ROLE):
        return isDefined(this.impliedSelfRole())
      case (PropertyId.FILE_VIRTUAL_SEARCH_RESULT):{
        console.error("Should never be checking for search relevance")
        return
      }
      case (PropertyId.FILE_VIRTUAL_TAGS):
        return any(
          Object.values(this.properties),
          p => groupFor(p!.propertyId as PropertyId) === PropertyGroup.FileTagMetaProperty,
        )
    }
    return isDefined(this.properties[property]) && !this.properties[property].deleted
  }

  hasDeletedProperty(property: PropertyId) {
    return isDefined(this.properties[property]) && !this.hasProperty(property)
  }

  hasGenerativeMetadata() {
    const properties = [
      PropertyId.FILE_GENERATION_PROMPT,
      PropertyId.FILE_GENERATION_NEGATIVE_PROMPT,
      PropertyId.FILE_GENERATION_SOURCE,
      PropertyId.FILE_GENERATION_MODEL,
      PropertyId.FILE_GENERATION_MODEL_VERSION,
      PropertyId.FILE_GENERATION_SEED,
    ]
    for (const propertyId of properties)
      if (this.property(propertyId))
        return true
    return false
  }

  imageMetadata() {
    const property = this.property(PropertyId.IMAGE_EXIF_JSON)
    if (!property)
      return
    if (property.value.type === PropertySchema.ExifJsonStringValue)
      return property.value.exif
    console.error("Exif metadata is malformed", property)
  }

  pdfMetadata() {
    const property = this.property(PropertyId.PDF_CONTAINER_METADATA)
    if (!property)
      return
    if (property.value.type === PropertySchema.FilePdfContainerMetadataValue)
      return property.value.metadata
    console.error("Pdf metadata is malformed", property)
  }

  mediaDurationMs() {
    const property = this.property(PropertyId.MEDIA_DURATION_MS)
    if (!property)
      return
    if (property.value.type === PropertySchema.NonNegativeIntegerValue)
      return property.value.nonNegativeInteger
    console.error("Media duration not available", property)
  }

  searchResult() {
    const property = this.property(PropertyId.FILE_VIRTUAL_SEARCH_RESULT)
    if (!property)
      return
    if (property.value.type === PropertySchema.SearchResultValue)
      return property.value.result
    console.error("Search relevance is malformed", property)
  }

  searchIndexingStatus() {
    const property = this.property(PropertyId.FILE_SEARCH_INDEXING_STATUS)
    if (!property)
      return
    if (property.value.type === PropertySchema.SearchIndexingStatusValue)
      return property.value.status
    console.error("Search indexing status is malformed", property)
  }

  audioMetadata() {
    const property = this.property(PropertyId.AUDIO_CONTAINER_METADATA)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileAudioContainerMetadataValue)
      return property.value.metadata
    console.error("Audio metadata is malformed", property)
  }

  videoMetadata() {
    const property = this.property(PropertyId.VIDEO_CONTAINER_METADATA)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileVideoContainerMetadataValue)
      return property.value.metadata
    console.error("Video metadata is malformed", property)
  }

  /** open graph link metadata */
  linkMetadata() {
    const property = this.property(PropertyId.LINK_OPEN_GRAPH_METADATA)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileLinkOpenGraphMetadataValue)
      return property.value.metadata
    console.error("link metadata is malformed", property)
  }

  thumbnailSet() {
    const property = this.property(PropertyId.THUMBNAIL_SET)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileThumbnailSetValue)
      return property.value.set
    console.error("Malformed URI", property)
    throw new Error("Malformed thumbnail Uri")
  }

  setModifiedAt(timestamp: string, author: string) {
    this.addProperty({
      id: this.id,
      propertyId: PropertyId.FILE_MODIFIED_AT,
      version: 0,
      status: PropertyStatus.StatusPending,
      timestamp,
      value: {
        type: PropertySchema.UtcTimestampMillisValue,
        utcTimestampMillis: asTimestampMillis(timestamp),
      },
      schema: PropertySchema.UtcTimestampMillisValue,
      author,
    })
  }

  setAccessedAt(timestamp: string, author: string) {
    this.addProperty({
      id: this.id,
      propertyId: PropertyId.FILE_ACCESSED_AT,
      version: 0,
      status: PropertyStatus.StatusPending,
      timestamp,
      value: {
        type: PropertySchema.UtcTimestampMillisValue,
        utcTimestampMillis: asTimestampMillis(timestamp),
      },
      schema: PropertySchema.UtcTimestampMillisValue,
      author,
    })
  }

  async subscribe(timeoutMs = 5000) {
    const requestId = generateWorkerRequestId()
    sendFolderSubscribeRequest([{
      requestId,
      folderId: this.id,
      offline: false,
    }])
    const { waitForFirstDataResponse } = useEvents()
    const response = await withTimeout(
      waitForFirstDataResponse("folderSubscribeResponse", requestId),
      timeoutMs,
    )
    return response
  }
}
